import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ACCORDION_TITLE_CLASS } from "@/lib/accordion-content";
import { Multipleprint } from "@/lib/types";
import { SupportedLocale } from "@/i18n";
import { useCallback, useState } from "react";
import { RelatedItemCard } from "@/pages/dashboard/shared/RelatedItemCard";

interface RelatedWorksAccordionProps {
  data: Multipleprint;
  locale: SupportedLocale;
  t: (key: string) => string;
}

export function RelatedWorksAccordion({
  data,
  locale,
  t,
}: RelatedWorksAccordionProps) {
  const [expandedDocs, setExpandedDocs] = useState<Record<string, boolean>>({});

  const handleToggleExpand = useCallback((id: string) => {
    setExpandedDocs((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  const relatedArtwork =
    data?.relatedArtwork?.filter(
      (artwork: any) => artwork?.oeuvresPublicationStatus === "published",
    ) ?? [];
  const relatedDocument =
    data?.relatedDocument?.filter(
      (doc: any) => doc?.documentsPublicationStatus === "published",
    ) ?? [];
  const relatedWriting =
    data?.relatedWriting?.filter(
      (writing: any) => writing?.ecritsPublicationStatus === "published",
    ) ?? [];
  const relatedPhotography =
    data?.relatedPhotography?.filter(
      (photo: any) => photo?.photosPublicationStatus === "published",
    ) ?? [];
  // Self-relation many-to-many : selon le côté où le lien a été créé, le multiple
  // lié est stocké dans `relatedMultipleprint` OU dans
  // `from_Multipleprint_relatedMultipleprint`. On fusionne les deux côtés (l'API
  // renvoie et enrichit les deux) puis on dédoublonne par id, sinon un lien créé
  // « dans l'autre sens » n'apparaissait pas ici.
  const relatedMultipleprint = (() => {
    const merged = [
      ...(data?.relatedMultipleprint ?? []),
      ...(((data as any)?.from_Multipleprint_relatedMultipleprint as any[]) ??
        []),
    ].filter(
      (multipleprint: any) =>
        multipleprint?.estampesMultiplesPublicationStatus === "published",
    );
    const seen = new Set<string>();
    return merged.filter((multipleprint: any) => {
      if (!multipleprint?.id || seen.has(multipleprint.id)) return false;
      seen.add(multipleprint.id);
      return true;
    });
  })();

  const relationNotEmpty =
    relatedArtwork.length > 0 ||
    relatedDocument.length > 0 ||
    relatedWriting.length > 0 ||
    relatedPhotography.length > 0 ||
    relatedMultipleprint.length > 0;

  if (!relationNotEmpty) return null;

  return (
    <AccordionItem value="oeuvres">
      <AccordionTrigger className={ACCORDION_TITLE_CLASS}>
        {t("singleMultipleprintDescriptionRelationLabel")}
      </AccordionTrigger>
      <AccordionContent className="flex flex-col gap-4 w-full">
        {relatedArtwork.map((artwork: any) => (
          <RelatedItemCard
            key={artwork.id}
            item={artwork}
            type="artwork"
            locale={locale}
            expandedDocs={expandedDocs}
            onToggleExpand={handleToggleExpand}
            t={t}
          />
        ))}
        {relatedDocument.map((doc: any) => (
          <RelatedItemCard
            key={doc.id}
            item={doc}
            type="document"
            locale={locale}
            expandedDocs={expandedDocs}
            onToggleExpand={handleToggleExpand}
            t={t}
          />
        ))}
        {relatedMultipleprint.map((mp: any) => (
          <RelatedItemCard
            key={mp.id}
            item={mp}
            type="multipleprint"
            locale={locale}
            expandedDocs={expandedDocs}
            onToggleExpand={handleToggleExpand}
            t={t}
          />
        ))}
        {relatedPhotography.map((ph: any) => (
          <RelatedItemCard
            key={ph.id}
            item={ph}
            type="photography"
            locale={locale}
            expandedDocs={expandedDocs}
            onToggleExpand={handleToggleExpand}
            t={t}
          />
        ))}
        {relatedWriting.map((w: any) => (
          <RelatedItemCard
            key={w.id}
            item={w}
            type="writing"
            locale={locale}
            expandedDocs={expandedDocs}
            onToggleExpand={handleToggleExpand}
            t={t}
          />
        ))}
      </AccordionContent>
    </AccordionItem>
  );
}
